import { useCallback } from "react";
import { logger } from "../../../utils/logger";
import { ethers, BigNumber } from "ethers";
import { getWeb3ProviderForChain } from "../../../utils/wallet/activeSigner";
import { GetOrders200Response, SideResponse } from "../../../codegen-api";
import { getCurrentChainConfig } from "../../../config/chains";
import { authApi } from "../../../services/api/apiFetcher";
import {
  PERP_ORDER_HANDLER_ABI,
  isOpenOrder,
  isPerpOrder,
  numericOrderId,
  sameMarket,
} from "./usePerpOrder.helpers";

interface UsePerpCancelHelpersArgs {
  account: string | null | undefined;
  apiKey: string | undefined;
  apiSecret: string | undefined;
  revalidatePerpDataSoon: () => void;
}

export const useCancelConflictingCloseOrders = ({
  account,
  apiKey,
  apiSecret,
  revalidatePerpDataSoon,
}: UsePerpCancelHelpersArgs) =>
  useCallback(
    async (
      position: any,
      market: string,
      isLong: boolean,
      orderHandlerContract: ethers.Contract
    ) => {
      if (!account || !apiKey || !apiSecret) {
        return [];
      }

      const makerSideThatWouldMatchClose = isLong
        ? SideResponse.Buy
        : SideResponse.Sell;
      const fetcher = authApi(apiKey, apiSecret);
      let orders: GetOrders200Response[] = [];
      try {
        const response = await (await fetcher.getOrders())();
        orders = response.data || [];
      } catch (error) {
        console.warn(
          "[PerpOrder] Unable to pre-check conflicting orders before close",
          error
        );
        return [];
      }

      const conflictingOrderIds = orders
        .filter((order) => {
          if (!isPerpOrder(order) || !isOpenOrder(order)) {
            return false;
          }
          if (!sameMarket(order, market, position.instrument_name)) {
            return false;
          }
          return (
            String(order.side || "").toLowerCase() ===
            makerSideThatWouldMatchClose
          );
        })
        .map((order) => numericOrderId(order.order_id))
        .filter((orderId): orderId is BigNumber => !!orderId);

      if (!conflictingOrderIds.length) {
        return [];
      }

      logger.debug("[PerpOrder] Cancelling self-match maker orders before close", {
        market,
        side: makerSideThatWouldMatchClose,
        orderIds: conflictingOrderIds.map((orderId) => orderId.toString()),
      });

      const estimatedGas =
        await orderHandlerContract.estimateGas.cancelOrderBatch(
          conflictingOrderIds,
          account
        );
      const tx = await orderHandlerContract.cancelOrderBatch(
        conflictingOrderIds,
        account,
        { gasLimit: estimatedGas.mul(120).div(100) }
      );
      await tx.wait(1);
      revalidatePerpDataSoon();

      return conflictingOrderIds.map((orderId) => orderId.toString());
    },
    [account, apiKey, apiSecret, revalidatePerpDataSoon]
  );

// Cancel the user's resting CLOSE-direction orders for a market before placing
// a new limit close. Without on-chain reduce-only, multiple resting reduce
// orders could sum to more than the position and flip it; keeping only one
// resting close at a time (each capped to the position) bounds aggregate to
// the position size.
export const useCancelRestingCloseOrders = ({
  account,
  apiKey,
  apiSecret,
  revalidatePerpDataSoon,
}: UsePerpCancelHelpersArgs) =>
  useCallback(
    async (position: any): Promise<string[]> => {
      if (!account || !apiKey || !apiSecret) return [];
      const config = getCurrentChainConfig();
      const orderHandler = config.contracts.perpetualDex?.orderHandler;
      if (!orderHandler) return [];

      const isLong =
        position.side === "buy" || position.side === SideResponse.Buy;
      const market = position.instrument_id;
      // A close order rests on the opposite side of the position.
      const closeSide = isLong ? SideResponse.Sell : SideResponse.Buy;

      const fetcher = authApi(apiKey, apiSecret);
      let orders: GetOrders200Response[] = [];
      try {
        const response = await (await fetcher.getOrders())();
        orders = response.data || [];
      } catch (error) {
        console.warn(
          "[PerpOrder] Unable to fetch orders before limit close",
          error
        );
        return [];
      }

      const ids = orders
        .filter(
          (order) =>
            isPerpOrder(order) &&
            isOpenOrder(order) &&
            sameMarket(order, market, position.instrument_name) &&
            String(order.side || "").toLowerCase() === closeSide
        )
        .map((order) => numericOrderId(order.order_id))
        .filter((id): id is BigNumber => !!id);

      if (!ids.length) return [];

      const provider = await getWeb3ProviderForChain(config);
      const signer = provider.getSigner();
      const orderHandlerContract = new ethers.Contract(
        orderHandler,
        PERP_ORDER_HANDLER_ABI,
        signer
      );
      const estimatedGas =
        await orderHandlerContract.estimateGas.cancelOrderBatch(ids, account);
      const tx = await orderHandlerContract.cancelOrderBatch(ids, account, {
        gasLimit: estimatedGas.mul(120).div(100),
      });
      await tx.wait(1);
      revalidatePerpDataSoon();
      return ids.map((id) => id.toString());
    },
    [account, apiKey, apiSecret, revalidatePerpDataSoon]
  );
